import * as React from 'react';

import { initiateChild, listenToChildMessages } from '@coursera-web/widget-connect';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { SvgButton, color } from '@coursera/coursera-ui';
import { SvgClose, SvgMaximize } from '@coursera/coursera-ui/svg';

import {
  addLoggedActionErrors,
  logAction,
  setAnswer,
  setFrameUnmounted,
  setManifest,
  updateLoggedAction,
} from 'bundles/widget-simulator/actions/WidgetSimulatorActions';
import type WidgetSimulatorStoreClass from 'bundles/widget-simulator/stores/WidgetSimulatorStore';
import type { UserSuppliedObject } from 'bundles/widget-simulator/types';
import type {
  GetStoredValuesRpcMessage,
  PluginRpcMessage,
  SetAnswerRpcMessage,
  SetGradedCompleteRpcMessage,
  SetHeightRpcMessage,
  SetStoredValuesRpcMessage,
} from 'bundles/widget-simulator/types/RpcMessages';
import * as FakeRpcApiUtils from 'bundles/widget-simulator/utils/FakeRpcApiUtils';
import Simulator from 'bundles/widget-simulator/utils/Simulator';

import _t from 'i18n!nls/widget-simulator';

import 'css!./__styles__/Frame';

const rpcActionTypes = [
  'LOAD_WIDGET_ERROR',
  'GET_SESSION_CONFIGURATION',
  'SET_STORED_VALUES',
  'GET_STORED_VALUES',
  'SET_ANSWER',
  'SET_HEIGHT',
  'SET_COMPLETE',
  'SET_GRADED_COMPLETE',
];

const FrameMessage = ({ text }: $TSFixMe) => <div className="rc-FrameMessage">{text}</div>;

// TODO: Type this and move it to another file.
type Manifest = Record<string, any>;

type PropsFromCaller = {
  defaultHeight?: string;
  widgetContentTitle?: string;
};

type PropsFromStores = {
  manifest: Manifest;
  url: string;
  sessionConfig: UserSuppliedObject;
  // TODO: What was this supposed to be used for?
  refreshTime: number;
};

export type PropsToComponent = PropsFromCaller & PropsFromStores;

type State = {
  specifiedHeight: string;
  expanded: boolean;
};

type FrameContainerProps = {
  className?: string;
  children: React.ReactNode;
};

const FrameContainer = ({ children, className }: FrameContainerProps) => {
  return <div className={classNames('rc-Frame', className)}>{children}</div>;
};

// TODO: This class is too big, and it's also tightly coupled with the plugin simulator. This
// would be a good opportunity to create a generic, pluggable Frame component that can be used
// in authoring, learner, etc.
export class Frame extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof Frame.contextTypes>;

  static defaultProps = {
    // TODO: Can this default height value be made official and standardized across all plugin frames?
    defaultHeight: '500px',

    // TODO: Figure out what should go here.
    get widgetContentTitle() {
      return _t('Plugin');
    },
  };

  state = {
    specifiedHeight: '',
    expanded: false,
  };

  componentDidMount() {
    const { url } = this.props;
    // TODO: Figure out what these values should be.
    const iFrameSrc = '*';
    const widgetId = 'widgetId123';

    this.setupSimulator();

    initiateChild(this.sendMessageToIFrame, widgetId, iFrameSrc).then(() => {
      this.removeListenerHandle = listenToChildMessages(
        widgetId,
        rpcActionTypes,
        this.onReceiveMessage,
        this.sendMessageToIFrame,
        iFrameSrc
      );
    });

    this.loadManifest(url);
  }

  UNSAFE_componentWillReceiveProps(nextProps: PropsToComponent) {
    const { url } = this.props;
    if (url !== nextProps.url) {
      this.loadManifest(nextProps.url);
    }
  }

  componentWillUnmount() {
    const { executeAction } = this.context;

    executeAction(setFrameUnmounted);

    if (this.removeListenerHandle) {
      this.removeListenerHandle();
    }
  }

  onExpandToggle = () => {
    // slightly hacky way of ensuring body (higher in hierarchy) can't be scrolled when widget expanded
    const { expanded } = this.state;
    if (!expanded) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }
    this.setState({ expanded: !expanded });
  };

  onReceiveMessage = (message: PluginRpcMessage): Promise<any> => {
    if (this.simulator) {
      return Promise.resolve(this.simulator.handleMessage(message));
    }

    // Execution should never reach this point.
    return Promise.resolve();
  };

  frame: any = undefined;

  removeListenerHandle: Function | undefined = undefined;

  simulator: Simulator | undefined = undefined;

  setupSimulator() {
    const { executeAction } = this.context;

    // TODO: Move this to another file.
    const simulator = new Simulator();

    simulator.addHandler('LOAD_WIDGET_ERROR', (message: PluginRpcMessage) => {
      executeAction(logAction, { name: 'LOAD_WIDGET_ERROR' });

      // TODO: When does this happen?
      // TODO: Do something with the error like showing it in the simulator UI.
      return Promise.resolve();
    });

    simulator.addHandler('GET_SESSION_CONFIGURATION', (message: PluginRpcMessage) => {
      const { sessionConfig } = this.props;
      const config = sessionConfig || {};
      const response = config;

      executeAction(logAction, { name: 'GET_SESSION_CONFIGURATION', type: 'request', response });
      executeAction(logAction, { name: 'GET_SESSION_CONFIGURATION', type: 'response', response });

      return Promise.resolve(config);
    });

    // REMOTE
    simulator.addHandler('SET_STORED_VALUES', (message: PluginRpcMessage) => {
      const typedMessage = message as SetStoredValuesRpcMessage;
      const { manifest } = this.props;
      const newValues = typedMessage.body;
      const request = newValues;

      return new Promise((resolve) => {
        executeAction(logAction, {
          name: 'SET_STORED_VALUES',
          type: 'request',
          request,
          onComplete(requestId: $TSFixMe) {
            resolve(
              FakeRpcApiUtils.setStoredValues(manifest, newValues).then((response) => {
                const unconfiguredKeys = Object.keys(newValues).filter((key) => !response.names.includes(key));

                if (unconfiguredKeys.length > 0) {
                  executeAction(addLoggedActionErrors, {
                    id: requestId,
                    errors: [
                      <span>
                        The following keys are not configured in your <code>manifest.json</code>:{' '}
                        {unconfiguredKeys.join(', ')}
                      </span>,
                    ],
                  });
                }

                executeAction(logAction, { name: 'SET_STORED_VALUES', type: 'response', response, request });
                executeAction(updateLoggedAction, { id: requestId, newProps: { response } });

                return response;
              })
            );
          },
        });
      });
    });

    // REMOTE
    simulator.addHandler('GET_STORED_VALUES', (message: PluginRpcMessage) => {
      const typedMessage = message as GetStoredValuesRpcMessage;
      const { manifest } = this.props;
      const names = typedMessage.body;
      const request = names;

      return new Promise((resolve) => {
        executeAction(logAction, {
          name: 'GET_STORED_VALUES',
          type: 'request',
          request,
          onComplete(requestId: string) {
            resolve(
              FakeRpcApiUtils.getStoredValues(manifest, names).then((response) => {
                const unconfiguredKeys = names.filter((key) => !(key in response));

                if (unconfiguredKeys.length > 0) {
                  executeAction(addLoggedActionErrors, {
                    id: requestId,
                    errors: [
                      // TODO: FormattedHTMLMessage
                      <span>
                        The following keys are not configured in your <code>manifest.json</code>:{' '}
                        {unconfiguredKeys.join(', ')}
                      </span>,
                    ],
                  });
                }

                executeAction(logAction, { name: 'GET_STORED_VALUES', type: 'response', response, request });
                executeAction(updateLoggedAction, { id: requestId, newProps: { response } });

                return response;
              })
            );
          },
        });
      });
    });

    // LOCAL
    simulator.addHandler('SET_HEIGHT', (message: PluginRpcMessage) => {
      const typedMessage = message as SetHeightRpcMessage;
      const { height } = typedMessage.body;
      this.setState({ specifiedHeight: height });

      executeAction(logAction, { name: 'SET_HEIGHT', type: 'request', request: height });

      return Promise.resolve();
    });

    simulator.addHandler('SET_ANSWER', (message: PluginRpcMessage) => {
      const typedMessage = message as SetAnswerRpcMessage;
      const answer = typedMessage.body;
      const request = answer;

      return new Promise<void>((resolve) => {
        executeAction(logAction, {
          name: 'SET_ANSWER',
          type: 'request',
          request,
          onComplete() {
            executeAction(setAnswer, { answer });
            resolve();
          },
        });
      });
    });

    // REMOTE
    // TODO: Allow the item to be uncleared.
    simulator.addHandler('SET_COMPLETE', (message: PluginRpcMessage) => {
      return new Promise<void>((resolve) => {
        // just log the action for now
        executeAction(logAction, {
          name: 'SET_COMPLETE',
          type: 'request',
          onComplete() {
            resolve();
          },
        });
      });
    });

    simulator.addHandler('SET_GRADED_COMPLETE', (message: PluginRpcMessage) => {
      return new Promise<void>((resolve) => {
        const typedMessage = message as SetGradedCompleteRpcMessage;
        const { rawScore, minScore, maxScore } = typedMessage.body;
        // just log the action for now
        executeAction(logAction, {
          name: 'SET_GRADED_COMPLETE',
          data: { rawScore, minScore, maxScore },
          type: 'request',
          onComplete() {
            resolve();
          },
        });
      });
    });
    this.simulator = simulator;
  }

  manifestDownloadedInitialized = false;

  loadManifest(url: $TSFixMe) {
    const { executeAction } = this.context;

    if (this.manifestDownloadedInitialized || !url) {
      return;
    }

    this.manifestDownloadedInitialized = true;

    fetch(`${url}/manifest.json`)
      .then((response) => response.json())
      .then((manifest) => {
        executeAction(setManifest, { manifest });
      })
      // TODO: Do something with this error value.
      .catch(() => {
        this.manifestDownloadedInitialized = false;
      });
  }

  sendMessageToIFrame = (message: $TSFixMe, src: $TSFixMe) => {
    if (this.frame && this.frame.contentWindow) {
      this.frame.contentWindow.postMessage(message, src);
    }
  };

  trySmartHeightDefault = () => {
    const { frame } = this;
    const { specifiedHeight } = this.state;

    if (!!frame && !specifiedHeight) {
      try {
        const contentsHeight = frame.contentWindow.document.body.scrollHeight;
        if (contentsHeight && contentsHeight > 0) {
          frame.height = contentsHeight + 'px';
        }
      } catch {
        // if we can't get the height of the contents, we'll keep our default height
      }
    }
  };

  render() {
    const { url, defaultHeight, widgetContentTitle } = this.props;
    const { specifiedHeight, expanded } = this.state;
    const height = specifiedHeight || defaultHeight;
    const width = '100%';

    // TODO: Take this from the manifest.
    const sandbox = 'allow-scripts allow-same-origin allow-forms allow-presentation';

    if (!url) {
      return <FrameMessage text={_t('Please supply a url')} />;
    }

    const iframeClassName = classNames({
      'default-height': !specifiedHeight,
    });
    const expandedClass = expanded ? 'expanded' : 'unexpanded';

    return (
      <div className="rc-FrameContainer">
        <FrameContainer className={expandedClass}>
          {expanded && <div className="dimmer" />}

          <div className="widget-container vertical-box">
            {expanded && (
              <div className="widget-title horizontal-box align-items-vertical-center">
                <span className="body-2-text">{widgetContentTitle}</span>
                <div className="flex-1" />
                <SvgButton
                  type="noStyle"
                  size="zero"
                  onClick={this.onExpandToggle}
                  svgElement={<SvgClose size={24} color={color.bgGrayThemeDark} />}
                />
              </div>
            )}
            <div className="frame-container">
              <iframe
                title={_t('Plugin')}
                src={url}
                className={iframeClassName}
                height={height}
                width={width}
                sandbox={sandbox}
                ref={(c) => {
                  this.frame = c;
                }}
                onLoad={this.trySmartHeightDefault}
              />
            </div>
          </div>
        </FrameContainer>

        <div className="frame-actions p-a-1">
          <SvgButton
            type="noStyle"
            size="zero"
            onClick={this.onExpandToggle}
            svgElement={<SvgMaximize size={25} color={color.primary} />}
          />
        </div>
      </div>
    );
  }
}

type Stores = {
  WidgetSimulatorStore: typeof WidgetSimulatorStoreClass;
};

export default compose<PropsToComponent, PropsFromCaller>(
  connectToStores<PropsFromStores, {}, Stores>(['WidgetSimulatorStore'], ({ WidgetSimulatorStore }) => ({
    manifest: WidgetSimulatorStore.getManifest(),
    url: WidgetSimulatorStore.getFullUrl(),
    sessionConfig: WidgetSimulatorStore.getCombinedSessionConfig(),
    refreshTime: WidgetSimulatorStore.getRefreshTime(),
  }))
)(Frame);
